<template>
  <vue-final-modal
    v-model="isOpen"
    classes="modal-overlay-container"
    content-class="modern-modal-content policy-settings-modal"
    @before-close="closeModal"
  >
    <div class="modal-header">
      <div class="header-text-container">
        <div class="title-wrapper">
          <div class="title-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="4" y1="21" x2="4" y2="14"></line>
              <line x1="4" y1="10" x2="4" y2="3"></line>
              <line x1="12" y1="21" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12" y2="3"></line>
              <line x1="20" y1="21" x2="20" y2="16"></line>
              <line x1="20" y1="12" x2="20" y2="3"></line>
              <line x1="1" y1="14" x2="7" y2="14"></line>
              <line x1="9" y1="8" x2="15" y2="8"></line>
              <line x1="17" y1="16" x2="23" y2="16"></line>
            </svg>
          </div>
          <div>
            <h5 class="modal-title">Policy Global Settings</h5>
            <small class="text-muted" style="font-size: 0.78rem;">Configure default variable values and organizational roles applied across policies</small>
          </div>
        </div>
      </div>
      <button type="button" class="close-button" @click="closeModal" aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <!-- Navigation Tabs -->
    <div class="settings-nav-tabs">
      <button
        type="button"
        class="nav-tab-btn"
        :class="{ active: activeTab === 'general' }"
        @click="activeTab = 'general'"
      >
        <i class="fas fa-building me-1"></i> General & Identity
      </button>
      <button
        type="button"
        class="nav-tab-btn"
        :class="{ active: activeTab === 'personnel' }"
        @click="activeTab = 'personnel'"
      >
        <i class="fas fa-user-tie me-1"></i> Key Personnel
      </button>
      <button
        type="button"
        class="nav-tab-btn"
        :class="{ active: activeTab === 'cloud' }"
        @click="activeTab = 'cloud'"
      >
        <i class="fas fa-cloud me-1"></i> Cloud & Infrastructure
      </button>
      <button
        type="button"
        class="nav-tab-btn"
        :class="{ active: activeTab === 'security' }"
        @click="activeTab = 'security'"
      >
        <i class="fas fa-shield-alt me-1"></i> Password & Security SLAs
      </button>
      <button
        type="button"
        class="nav-tab-btn"
        :class="{ active: activeTab === 'retention' }"
        @click="activeTab = 'retention'"
      >
        <i class="fas fa-clock me-1"></i> Retention & Reviews
      </button>
    </div>

    <div class="modal-body">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
        <div class="text-muted mt-2 small">Loading global policy settings...</div>
      </div>

      <div v-else>
        <!-- TAB 1: General & Identity -->
        <div v-show="activeTab === 'general'">
          <div class="explainer-box mb-3">
            <strong>Organisational Details</strong> set default values for global variables across your Policy Library and compliance documentation.
          </div>

          <div class="row g-3">
            <div class="col-md-6">
              <div class="setting-item">
                <label for="orgName">Organisation Name <span class="text-danger">*</span></label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-building"></i></div>
                  <input
                    id="orgName"
                    v-model="form.organisation_name"
                    type="text"
                    class="form-control"
                    placeholder="e.g. Acme Corporation"
                  />
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="setting-item">
                <label for="idpSelect">Identity Provider (IdP)</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-fingerprint"></i></div>
                  <select id="idpSelect" class="form-control" v-model="form.identity_provider">
                    <option value="Microsoft Entra ID">Microsoft Entra ID (Azure AD)</option>
                    <option value="Okta">Okta</option>
                    <option value="Google Workspace">Google Workspace</option>
                    <option value="JumpCloud">JumpCloud</option>
                    <option value="Ping Identity">Ping Identity</option>
                    <option value="OneLogin">OneLogin</option>
                    <option value="Other / Custom">Other / Custom</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="col-12">
              <div class="setting-item">
                <label for="physicalAddress">Physical Address / Headquarters</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-map-marker-alt"></i></div>
                  <input
                    id="physicalAddress"
                    v-model="form.physical_address"
                    type="text"
                    class="form-control"
                    placeholder="e.g. Level 12, 100 Queen Street, Melbourne VIC 3000"
                  />
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="setting-item">
                <label for="itSupportEmail">IT Support Email</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-envelope"></i></div>
                  <input
                    id="itSupportEmail"
                    v-model="form.it_support_email"
                    type="email"
                    class="form-control"
                    placeholder="e.g. support@example.com"
                  />
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="setting-item">
                <label for="vulnEmail">Vulnerability Reporting Email</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-shield-virus"></i></div>
                  <input
                    id="vulnEmail"
                    v-model="form.vulnerability_reporting_email"
                    type="email"
                    class="form-control"
                    placeholder="e.g. security-report@example.com"
                  />
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="setting-item">
                <label for="techArchUrl">Technology Architecture (URL)</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-sitemap"></i></div>
                  <input
                    id="techArchUrl"
                    v-model="form.tech_architecture_url"
                    type="text"
                    class="form-control"
                    placeholder="e.g. https://wiki.example.com/tech-architecture"
                  />
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="setting-item">
                <label for="assetRegisters">Authorised Asset Registers</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-boxes"></i></div>
                  <input
                    id="assetRegisters"
                    v-model="form.authorised_asset_registers"
                    type="text"
                    class="form-control"
                    placeholder="e.g. Asset Panda / Jira Assets / Inventory Link"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- TAB 2: Key Personnel & Roles -->
        <div v-show="activeTab === 'personnel'">
          <div class="explainer-box mb-3">
            <strong>Key Personnel & Roles</strong> allow you to map organisational responsibilities. Choose whether each role outputs a standard <strong>Role Title</strong>, a <strong>Mapped Active User</strong>, or a <strong>Custom Name</strong> in policies.
          </div>

          <div class="personnel-grid">
            <div
              v-for="roleConfig in personnelList"
              :key="roleConfig.key"
              class="personnel-card p-3 rounded border mb-2 bg-light-surface"
            >
              <div class="d-flex justify-content-between align-items-center mb-2">
                <span class="fw-bold text-dark role-header-title">
                  <i :class="roleConfig.icon" class="text-theme me-2"></i>
                  {{ roleConfig.label }}
                </span>
                <div class="btn-group btn-group-sm mode-toggle-group">
                  <button
                    type="button"
                    class="btn"
                    :class="form.key_personnel[roleConfig.key].assignment_mode === 'role_title' ? 'btn-primary' : 'btn-outline-secondary'"
                    @click="setAssignmentMode(roleConfig.key, 'role_title')"
                  >
                    Role Title
                  </button>
                  <button
                    type="button"
                    class="btn"
                    :class="form.key_personnel[roleConfig.key].assignment_mode === 'mapped_user' ? 'btn-primary' : 'btn-outline-secondary'"
                    @click="setAssignmentMode(roleConfig.key, 'mapped_user')"
                  >
                    Assign User
                  </button>
                  <button
                    type="button"
                    class="btn"
                    :class="form.key_personnel[roleConfig.key].assignment_mode === 'custom_text' ? 'btn-primary' : 'btn-outline-secondary'"
                    @click="setAssignmentMode(roleConfig.key, 'custom_text')"
                  >
                    Custom Text
                  </button>
                </div>
              </div>

              <!-- Content according to mode -->
              <div class="row g-2 mt-1">
                <!-- Role Title Mode -->
                <div v-if="form.key_personnel[roleConfig.key].assignment_mode === 'role_title'" class="col-12">
                  <div class="input-with-icon">
                    <div class="input-icon"><i class="fas fa-tag"></i></div>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="form.key_personnel[roleConfig.key].role_title"
                      :placeholder="roleConfig.defaultTitle"
                    />
                  </div>
                </div>

                <!-- Mapped User Mode -->
                <div v-else-if="form.key_personnel[roleConfig.key].assignment_mode === 'mapped_user'" class="col-12">
                  <div class="input-with-icon">
                    <div class="input-icon"><i class="fas fa-user-check"></i></div>
                    <select
                      class="form-control form-control-sm"
                      v-model="form.key_personnel[roleConfig.key].user_id"
                      @change="onUserMappedChange(roleConfig.key)"
                    >
                      <option :value="null">-- Select an active user --</option>
                      <option v-for="user in users" :key="user.id" :value="user.id">
                        {{ user.first_name }} {{ user.last_name }} ({{ user.email }})
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Custom Text Mode -->
                <div v-else class="col-12">
                  <div class="input-with-icon">
                    <div class="input-icon"><i class="fas fa-edit"></i></div>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="form.key_personnel[roleConfig.key].custom_value"
                      placeholder="Enter person name or external contact"
                    />
                  </div>
                </div>

                <!-- Additional contact fields for DPO and Emergency Contact -->
                <template v-if="roleConfig.hasContactInfo">
                  <div class="col-md-6 mt-2">
                    <div class="input-with-icon">
                      <div class="input-icon"><i class="fas fa-envelope"></i></div>
                      <input
                        type="email"
                        class="form-control form-control-sm"
                        v-model="form.key_personnel[roleConfig.key].email"
                        :placeholder="roleConfig.label + ' Email'"
                      />
                    </div>
                  </div>
                  <div class="col-md-6 mt-2">
                    <div class="input-with-icon">
                      <div class="input-icon"><i class="fas fa-phone"></i></div>
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        v-model="form.key_personnel[roleConfig.key].phone"
                        :placeholder="roleConfig.label + ' Phone / Mobile'"
                      />
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- TAB 3: Cloud & Infrastructure -->
        <div v-show="activeTab === 'cloud'">
          <div class="explainer-box mb-3">
            <strong>Approved Cloud Services & Backup Infrastructure</strong> defaults used in Acceptable Use, Asset Management, and Backup & Disaster Recovery policies.
          </div>

          <div class="row g-3 mb-4">
            <div class="col-md-6">
              <div class="setting-item">
                <label for="backupStorageMethod">Backup Storage Method</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-hdd"></i></div>
                  <select id="backupStorageMethod" class="form-control" v-model="form.backup_storage_method">
                    <option value="Cloud Storage">Cloud Storage (e.g. AWS S3, Azure Blob)</option>
                    <option value="On-Premises Server">On-Premises Server / NAS</option>
                    <option value="Physical External Media">Physical External Media / Encrypted Drive</option>
                    <option value="Co-Location Data Centre">Co-Location Data Centre</option>
                  </select>
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="setting-item">
                <label>Backup Locations (Countries)</label>
                <div class="tag-selection-container p-2 border rounded bg-white">
                  <div class="d-flex flex-wrap gap-1">
                    <span
                      v-for="country in availableCountries"
                      :key="country"
                      class="badge tag-pill pointer"
                      :class="form.backup_locations.includes(country) ? 'bg-primary text-white' : 'bg-light text-dark border'"
                      @click="toggleBackupLocation(country)"
                    >
                      <i v-if="form.backup_locations.includes(country)" class="fas fa-check me-1"></i>
                      {{ country }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Approved Cloud Services List Builder -->
          <div class="setting-item">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <label class="mb-0"><i class="fas fa-cubes text-theme me-1"></i> Approved Cloud Services List</label>
              <button type="button" class="btn btn-sm btn-outline-primary" @click="addCloudService">
                <i class="fas fa-plus me-1"></i> Add Cloud Service
              </button>
            </div>

            <div v-if="form.approved_cloud_services.length === 0" class="text-center p-4 border rounded bg-light-surface text-muted small">
              No approved cloud services added yet. Click <strong>"Add Cloud Service"</strong> above to register cloud tools.
            </div>

            <div v-else class="cloud-services-list">
              <div
                v-for="(service, idx) in form.approved_cloud_services"
                :key="idx"
                class="cloud-service-card p-3 rounded border mb-2 bg-white"
              >
                <div class="row g-2 align-items-center">
                  <div class="col-md-4">
                    <label class="small text-muted mb-1 font-weight-bold">Cloud Service Name</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="service.service_name"
                      list="commonCloudServicesList"
                      placeholder="e.g. Microsoft 365, AWS"
                    />
                  </div>

                  <div class="col-md-4">
                    <label class="small text-muted mb-1 font-weight-bold">Data Location(s)</label>
                    <div class="input-with-icon">
                      <div class="input-icon"><i class="fas fa-globe small"></i></div>
                      <input
                        type="text"
                        class="form-control form-control-sm"
                        :value="service.data_locations ? service.data_locations.join(', ') : ''"
                        @input="updateServiceLocations(idx, $event.target.value)"
                        placeholder="e.g. Australia, US"
                      />
                    </div>
                  </div>

                  <div class="col-md-3">
                    <label class="small text-muted mb-1 font-weight-bold">Retention Period</label>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      v-model="service.retention_period"
                      placeholder="e.g. 7 years, 90 days"
                    />
                  </div>

                  <div class="col-md-1 text-end">
                    <label class="small text-muted mb-1 d-block">&nbsp;</label>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      title="Remove service"
                      @click="removeCloudService(idx)"
                    >
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <datalist id="commonCloudServicesList">
              <option value="Microsoft 365" />
              <option value="Amazon Web Services (AWS)" />
              <option value="Google Cloud Platform (GCP)" />
              <option value="Google Workspace" />
              <option value="Microsoft Azure" />
              <option value="Salesforce" />
              <option value="HubSpot" />
              <option value="Atlassian (Jira / Confluence)" />
              <option value="GitHub" />
              <option value="GitLab" />
              <option value="Slack" />
              <option value="Snowflake" />
              <option value="Cloudflare" />
              <option value="Oracle Cloud" />
            </datalist>
          </div>
        </div>

        <!-- TAB 4: Password & Security SLAs -->
        <div v-show="activeTab === 'security'">
          <div class="explainer-box mb-3">
            <strong>Password Security & Incident SLAs</strong> defaults for Access Control and Incident Response policies.
          </div>

          <div class="row g-3">
            <div class="col-md-4">
              <div class="setting-item">
                <label for="pwdMinLength">Password Min Length (# chars)</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-key"></i></div>
                  <input
                    id="pwdMinLength"
                    v-model.number="form.password_min_length"
                    type="number"
                    min="8"
                    max="64"
                    class="form-control"
                    placeholder="12"
                  />
                </div>
              </div>
            </div>

            <div class="col-md-4">
              <div class="setting-item">
                <label for="pwdExpiry">Password Expiry (Days)</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-calendar-times"></i></div>
                  <input
                    id="pwdExpiry"
                    v-model.number="form.password_expiry_days"
                    type="number"
                    min="1"
                    :disabled="form.password_never_expires"
                    class="form-control"
                    placeholder="90"
                  />
                </div>
              </div>
            </div>

            <div class="col-md-4">
              <div class="setting-item">
                <label>Never Expire Passwords</label>
                <div class="d-flex align-items-center justify-content-between p-2 border rounded bg-light-surface">
                  <span class="small text-muted">{{ form.password_never_expires ? 'Passwords never expire' : 'Enforce periodic expiry' }}</span>
                  <label class="pure-toggle mb-0">
                    <input type="checkbox" v-model="form.password_never_expires">
                    <span class="pure-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Password Complexity -->
            <div class="col-12">
              <div class="setting-item">
                <label>Password Complexity Requirements</label>
                <div class="d-flex flex-wrap gap-2 p-2 border rounded bg-white">
                  <span
                    v-for="comp in complexityOptions"
                    :key="comp"
                    class="badge tag-pill pointer"
                    :class="form.password_complexity.includes(comp) ? 'bg-primary text-white' : 'bg-light text-dark border'"
                    @click="toggleComplexity(comp)"
                  >
                    <i v-if="form.password_complexity.includes(comp)" class="fas fa-check me-1"></i>
                    {{ comp }}
                  </span>
                </div>
              </div>
            </div>

            <!-- MFA Methods -->
            <div class="col-12">
              <div class="setting-item">
                <label>Multi-Factor Authentication (MFA) Allowed Methods</label>
                <div class="d-flex flex-wrap gap-2 p-2 border rounded bg-white">
                  <span
                    v-for="method in mfaOptions"
                    :key="method"
                    class="badge tag-pill pointer"
                    :class="form.mfa_methods.includes(method) ? 'bg-primary text-white' : 'bg-light text-dark border'"
                    @click="toggleMfaMethod(method)"
                  >
                    <i v-if="form.mfa_methods.includes(method)" class="fas fa-check me-1"></i>
                    {{ method }}
                  </span>
                </div>
              </div>
            </div>

            <div class="col-md-4">
              <div class="setting-item">
                <label for="inactivityTimeout">User Inactivity Timeout</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-stopwatch"></i></div>
                  <input
                    id="inactivityTimeout"
                    v-model="form.user_inactivity_timeout"
                    type="text"
                    class="form-control"
                    placeholder="e.g. 15 minutes"
                  />
                </div>
              </div>
            </div>

            <div class="col-md-4">
              <div class="setting-item">
                <label for="incidentSla">Incident Internal Reporting SLA</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-bell"></i></div>
                  <input
                    id="incidentSla"
                    v-model="form.incident_reporting_sla"
                    type="text"
                    class="form-control"
                    placeholder="e.g. 24 hours"
                  />
                </div>
              </div>
            </div>

            <div class="col-md-4">
              <div class="setting-item">
                <label for="breachSla">Breach Notification SLA</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-exclamation-triangle"></i></div>
                  <input
                    id="breachSla"
                    v-model="form.breach_notification_sla"
                    type="text"
                    class="form-control"
                    placeholder="e.g. 72 hours"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- TAB 5: Retention & Reviews -->
        <div v-show="activeTab === 'retention'">
          <div class="explainer-box mb-3">
            <strong>Data Retention & Policy Review Cycles</strong> configure standard schedules for Data Classification, Archiving, and Audit Governance.
          </div>

          <div class="row g-3">
            <div class="col-md-6">
              <div class="setting-item">
                <label for="policyReviewFreq">Default Policy Review Frequency</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-sync-alt"></i></div>
                  <select id="policyReviewFreq" class="form-control" v-model="form.policy_review_frequency">
                    <option value="Annually">Annually (Every 12 Months)</option>
                    <option value="Bi-Annually">Bi-Annually (Every 24 Months)</option>
                    <option value="Semiannually">Semiannually (Every 6 Months)</option>
                    <option value="Quarterly">Quarterly (Every 3 Months)</option>
                  </select>
                </div>
                <small class="text-muted d-block mt-1" style="font-size: 0.74rem;">
                  Automatically populates the Next Review Date field when creating or reviewing policies.
                </small>
              </div>
            </div>

            <div class="col-md-6">
              <div class="setting-item">
                <label for="trainingFreq">Training Frequency</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-graduation-cap"></i></div>
                  <input
                    id="trainingFreq"
                    v-model="form.training_frequency"
                    type="text"
                    class="form-control"
                    placeholder="e.g. 1 time every quarter"
                  />
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="setting-item">
                <label for="backupFreq">Backup Frequency</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-history"></i></div>
                  <input
                    id="backupFreq"
                    v-model="form.backup_frequency"
                    type="text"
                    class="form-control"
                    placeholder="e.g. 1 time every day"
                  />
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="setting-item">
                <label for="backupRetention">Backup Retention Period</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-archive"></i></div>
                  <input
                    id="backupRetention"
                    v-model="form.backup_retention"
                    type="text"
                    class="form-control"
                    placeholder="e.g. 90 days / 1 year"
                  />
                </div>
              </div>
            </div>

            <div class="col-md-4">
              <div class="setting-item">
                <label for="financialRetention">Financial Data Retention</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-file-invoice-dollar"></i></div>
                  <input
                    id="financialRetention"
                    v-model="form.financial_data_retention"
                    type="text"
                    class="form-control"
                    placeholder="e.g. 7 years"
                  />
                </div>
              </div>
            </div>

            <div class="col-md-4">
              <div class="setting-item">
                <label for="employeeRetention">Employee Data Retention</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-id-card"></i></div>
                  <input
                    id="employeeRetention"
                    v-model="form.employee_data_retention"
                    type="text"
                    class="form-control"
                    placeholder="e.g. 7 years"
                  />
                </div>
              </div>
            </div>

            <div class="col-md-4">
              <div class="setting-item">
                <label for="auditRetention">Audit Log Retention</label>
                <div class="input-with-icon">
                  <div class="input-icon"><i class="fas fa-list-alt"></i></div>
                  <input
                    id="auditRetention"
                    v-model="form.audit_log_retention"
                    type="text"
                    class="form-control"
                    placeholder="e.g. 1 year"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-footer d-flex justify-content-between align-items-center">
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        @click="resetToDefaults"
        :disabled="saving || loading"
      >
        <i class="fas fa-undo me-1"></i> Reset Defaults
      </button>

      <div class="d-flex gap-2">
        <button type="button" class="btn btn-secondary" @click="closeModal" :disabled="saving">
          Cancel
        </button>
        <button type="button" class="btn btn-primary" @click="handleSave" :disabled="saving || loading">
          <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status"></span>
          {{ saving ? 'Saving...' : 'Save Settings' }}
        </button>
      </div>
    </div>
  </vue-final-modal>

  <ConfirmModal
    v-model="showResetConfirm"
    type="reset"
    title="Reset to System Defaults"
    message="Are you sure you want to reset all variable fields to their system defaults? Any unsaved changes will be replaced."
    confirm-button-text="Reset Defaults"
    confirm-variant="warning"
    @confirm="onConfirmReset"
  />
</template>

<script setup>
import { ref, reactive, computed, watch, getCurrentInstance, defineEmits, defineProps } from 'vue';
import ConfirmModal from '@/components/ConfirmModal.vue';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  orgId: {
    type: [Number, String],
    required: true
  },
  users: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:modelValue', 'saved']);
const { proxy } = getCurrentInstance();

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const activeTab = ref('general');
const loading = ref(false);
const saving = ref(false);
const showResetConfirm = ref(false);

const availableCountries = [
  'Australia',
  'United States',
  'United Kingdom',
  'European Union',
  'Singapore',
  'Canada',
  'Japan',
  'Germany',
  'New Zealand',
  'Ireland'
];

const complexityOptions = [
  'Uppercase',
  'Lowercase',
  'Numbers',
  'Special Characters'
];

const mfaOptions = [
  'Authenticator App',
  'Mobile Push Notification',
  'Hardware Token',
  'SMS',
  'Passkey',
  'PIN',
  'Email OTP',
  'Password/Passphrase',
  'Security Questions',
  'Fingerprint',
  'Facial Recognition'
];

const personnelList = [
  { key: 'ceo', label: 'CEO (Chief Executive Officer)', defaultTitle: 'Chief Executive Officer', icon: 'fas fa-user-tie', hasContactInfo: false },
  { key: 'cto', label: 'CTO (Chief Technology Officer)', defaultTitle: 'Chief Technology Officer', icon: 'fas fa-laptop-code', hasContactInfo: false },
  { key: 'cio', label: 'CIO (Chief Information Officer)', defaultTitle: 'Chief Information Officer', icon: 'fas fa-network-wired', hasContactInfo: false },
  { key: 'ciso', label: 'CISO (Chief Information Security Officer)', defaultTitle: 'Chief Information Security Officer', icon: 'fas fa-user-shield', hasContactInfo: false },
  { key: 'dpo', label: 'DPO (Data Protection Officer)', defaultTitle: 'Data Protection Officer', icon: 'fas fa-user-lock', hasContactInfo: true },
  { key: 'ism', label: 'ISM (Information Security Manager)', defaultTitle: 'Information Security Manager', icon: 'fas fa-shield-alt', hasContactInfo: false },
  { key: 'it_dept_head', label: 'IT Department Head', defaultTitle: 'IT Department Head', icon: 'fas fa-server', hasContactInfo: false },
  { key: 'hr_manager', label: 'HR Manager', defaultTitle: 'HR Manager', icon: 'fas fa-users-cog', hasContactInfo: false },
  { key: 'emergency_contact', label: 'Emergency Security Contact', defaultTitle: 'Emergency Security Contact', icon: 'fas fa-phone-alt', hasContactInfo: true },
  { key: 'cro', label: 'Chief Risk Officer (CRO) / Risk Manager', defaultTitle: 'Chief Risk Officer', icon: 'fas fa-balance-scale', hasContactInfo: false },
  { key: 'legal_counsel', label: 'Legal Counsel', defaultTitle: 'Legal Counsel', icon: 'fas fa-gavel', hasContactInfo: false },
  { key: 'compliance_officer', label: 'Compliance Officer', defaultTitle: 'Compliance Officer', icon: 'fas fa-clipboard-check', hasContactInfo: false }
];

const defaultFormState = () => ({
  organisation_name: '',
  physical_address: '',
  tech_architecture_url: '',
  it_support_email: '',
  vulnerability_reporting_email: '',
  authorised_asset_registers: '',
  backup_storage_method: 'Cloud Storage',
  identity_provider: 'Microsoft Entra ID',
  password_min_length: 12,
  password_expiry_days: 90,
  password_never_expires: false,
  incident_reporting_sla: '24 hours',
  breach_notification_sla: '72 hours',
  user_inactivity_timeout: '15 minutes',
  financial_data_retention: '7 years',
  employee_data_retention: '7 years',
  audit_log_retention: '1 year',
  backup_retention: '90 days',
  backup_frequency: '1 time every day',
  training_frequency: '1 time every quarter',
  policy_review_frequency: 'Annually',
  backup_locations: ['Australia'],
  password_complexity: ['Uppercase', 'Lowercase', 'Numbers', 'Special Characters'],
  mfa_methods: ['Authenticator App', 'Mobile Push Notification', 'Hardware Token'],
  approved_cloud_services: [
    { service_name: 'Microsoft 365', data_locations: ['Australia'], retention_period: '7 years' }
  ],
  key_personnel: {
    ceo: { assignment_mode: 'role_title', role_title: 'Chief Executive Officer', custom_value: '', user_id: null, email: '', phone: '' },
    cto: { assignment_mode: 'role_title', role_title: 'Chief Technology Officer', custom_value: '', user_id: null, email: '', phone: '' },
    cio: { assignment_mode: 'role_title', role_title: 'Chief Information Officer', custom_value: '', user_id: null, email: '', phone: '' },
    ciso: { assignment_mode: 'role_title', role_title: 'Chief Information Security Officer', custom_value: '', user_id: null, email: '', phone: '' },
    dpo: { assignment_mode: 'role_title', role_title: 'Data Protection Officer', custom_value: '', user_id: null, email: '', phone: '' },
    ism: { assignment_mode: 'role_title', role_title: 'Information Security Manager', custom_value: '', user_id: null, email: '', phone: '' },
    it_dept_head: { assignment_mode: 'role_title', role_title: 'IT Department Head', custom_value: '', user_id: null, email: '', phone: '' },
    hr_manager: { assignment_mode: 'role_title', role_title: 'HR Manager', custom_value: '', user_id: null, email: '', phone: '' },
    emergency_contact: { assignment_mode: 'role_title', role_title: 'Emergency Security Contact', custom_value: '', user_id: null, email: '', phone: '' },
    cro: { assignment_mode: 'role_title', role_title: 'Chief Risk Officer / Risk Manager', custom_value: '', user_id: null, email: '', phone: '' },
    legal_counsel: { assignment_mode: 'role_title', role_title: 'Legal Counsel', custom_value: '', user_id: null, email: '', phone: '' },
    compliance_officer: { assignment_mode: 'role_title', role_title: 'Compliance Officer', custom_value: '', user_id: null, email: '', phone: '' }
  }
});

const form = reactive(defaultFormState());

const toggleBackupLocation = (country) => {
  const idx = form.backup_locations.indexOf(country);
  if (idx > -1) {
    form.backup_locations.splice(idx, 1);
  } else {
    form.backup_locations.push(country);
  }
};

const toggleComplexity = (comp) => {
  const idx = form.password_complexity.indexOf(comp);
  if (idx > -1) {
    form.password_complexity.splice(idx, 1);
  } else {
    form.password_complexity.push(comp);
  }
};

const toggleMfaMethod = (method) => {
  const idx = form.mfa_methods.indexOf(method);
  if (idx > -1) {
    form.mfa_methods.splice(idx, 1);
  } else {
    form.mfa_methods.push(method);
  }
};

const addCloudService = () => {
  form.approved_cloud_services.push({
    service_name: '',
    data_locations: ['Australia'],
    retention_period: '7 years'
  });
};

const removeCloudService = (index) => {
  form.approved_cloud_services.splice(index, 1);
};

const updateServiceLocations = (index, val) => {
  if (!form.approved_cloud_services[index]) return;
  form.approved_cloud_services[index].data_locations = val
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

const setAssignmentMode = (roleKey, mode) => {
  if (!form.key_personnel[roleKey]) return;
  form.key_personnel[roleKey].assignment_mode = mode;
  if (mode === 'mapped_user' && form.key_personnel[roleKey].user_id) {
    onUserMappedChange(roleKey);
  }
};

const onUserMappedChange = (roleKey) => {
  const entry = form.key_personnel[roleKey];
  if (!entry || !entry.user_id) return;
  const user = props.users.find((u) => u.id === entry.user_id);
  if (user) {
    if (!entry.email || entry.assignment_mode === 'mapped_user') {
      entry.email = user.email || '';
    }
  }
};

const fetchSettings = async () => {
  if (!props.orgId) return;
  loading.value = true;
  try {
    const res = await proxy.$axios.get(`/orgadmin/trainings/organisations/${props.orgId}/policy-settings`);
    if (res.data.success && res.data.policy_setting) {
      const data = res.data.policy_setting;
      Object.assign(form, {
        organisation_name: data.organisation_name || '',
        physical_address: data.physical_address || '',
        tech_architecture_url: data.tech_architecture_url || '',
        it_support_email: data.it_support_email || '',
        vulnerability_reporting_email: data.vulnerability_reporting_email || '',
        authorised_asset_registers: data.authorised_asset_registers || '',
        backup_storage_method: data.backup_storage_method || 'Cloud Storage',
        identity_provider: data.identity_provider || 'Microsoft Entra ID',
        password_min_length: data.password_min_length || 12,
        password_expiry_days: data.password_expiry_days || 90,
        password_never_expires: !!data.password_never_expires,
        incident_reporting_sla: data.incident_reporting_sla || '24 hours',
        breach_notification_sla: data.breach_notification_sla || '72 hours',
        user_inactivity_timeout: data.user_inactivity_timeout || '15 minutes',
        financial_data_retention: data.financial_data_retention || '7 years',
        employee_data_retention: data.employee_data_retention || '7 years',
        audit_log_retention: data.audit_log_retention || '1 year',
        backup_retention: data.backup_retention || '90 days',
        backup_frequency: data.backup_frequency || '1 time every day',
        training_frequency: data.training_frequency || '1 time every quarter',
        policy_review_frequency: data.policy_review_frequency || 'Annually',
        backup_locations: data.backup_locations || ['Australia'],
        password_complexity: data.password_complexity || ['Uppercase', 'Lowercase', 'Numbers', 'Special Characters'],
        mfa_methods: data.mfa_methods || ['Authenticator App', 'Mobile Push Notification', 'Hardware Token'],
        approved_cloud_services: data.approved_cloud_services || []
      });

      if (data.key_personnel) {
        Object.keys(form.key_personnel).forEach((k) => {
          if (data.key_personnel[k]) {
            Object.assign(form.key_personnel[k], data.key_personnel[k]);
          }
        });
      }
    }
  } catch (err) {
    console.error('Failed to load global policy settings', err);
    proxy.$toast.error('Could not load policy global settings');
  } finally {
    loading.value = false;
  }
};

const resetToDefaults = () => {
  showResetConfirm.value = true;
};

const onConfirmReset = () => {
  const defaults = defaultFormState();
  Object.assign(form, defaults);
  proxy.$toast.info('Settings reset to defaults (click Save to apply).');
};

const handleSave = async () => {
  saving.value = true;
  try {
    const payload = JSON.parse(JSON.stringify(form));
    const res = await proxy.$axios.post(
      `/orgadmin/trainings/organisations/${props.orgId}/policy-settings`,
      payload
    );

    if (res.data.success) {
      proxy.$toast.success('Policy global settings saved successfully.');
      emit('saved', res.data.policy_setting);
      closeModal();
    } else {
      proxy.$toast.error(res.data.message || 'Failed to save settings');
    }
  } catch (err) {
    console.error('Error saving policy global settings', err);
    proxy.$toast.error('An error occurred while saving global policy settings.');
  } finally {
    saving.value = false;
  }
};

const closeModal = () => {
  isOpen.value = false;
};

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) {
      activeTab.value = 'general';
      fetchSettings();
    }
  }
);
</script>

<style scoped>
/* Modal Container & Content */
:deep(.modal-overlay-container) {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1000;
}

:deep(.policy-settings-modal) {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 860px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  border: none !important;
  padding: 0;
  margin: 0 1rem;
  overflow: hidden;
  max-height: 90vh;
}

/* Header */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
  background-color: #ffffff;
}

.title-wrapper {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.title-icon {
  color: #00a0c1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 160, 193, 0.1);
  padding: 8px;
  border-radius: 8px;
}

.modal-title {
  color: #1e293b;
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.close-button {
  background: #f1f5f9;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #94a3b8;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0;
}

.close-button:hover {
  background-color: #fee2e2;
  color: #dc3545;
  transform: rotate(90deg);
}

/* Nav Tabs */
.settings-nav-tabs {
  display: flex;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 0.25rem 1rem 0;
  gap: 0.35rem;
  overflow-x: auto;
  white-space: nowrap;
}

.nav-tab-btn {
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  padding: 0.65rem 0.9rem;
  font-size: 0.83rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px 4px 0 0;
}

.nav-tab-btn:hover {
  color: #00a0c1;
  background-color: rgba(0, 160, 193, 0.05);
}

.nav-tab-btn.active {
  color: #00a0c1;
  border-bottom-color: #00a0c1;
  background-color: #ffffff;
}

/* Body */
.modal-body {
  padding: 1.25rem 1.5rem;
  flex-grow: 1;
  overflow-y: auto;
  max-height: calc(90vh - 180px);
  overflow-x: hidden;
}

.explainer-box {
  padding: 0.75rem 1rem;
  background-color: #f8fafc;
  border-left: 4px solid #00a0c1;
  border-radius: 0 6px 6px 0;
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.5;
}

.explainer-box strong {
  color: #334155;
  font-weight: 600;
}

.setting-item {
  margin-bottom: 0.85rem;
}

.setting-item label {
  display: block;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  color: #64748b;
  margin-bottom: 0.35rem;
  font-weight: 700;
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 1;
}

.form-control {
  width: 100%;
  padding: 0.6rem 0.7rem 0.6rem 2.4rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #1e293b;
  background-color: #ffffff;
  transition: all 0.2s;
  appearance: auto;
}

.form-control-sm {
  padding: 0.4rem 0.6rem 0.4rem 2rem;
  font-size: 0.82rem;
}

.form-control:focus {
  border-color: #00a0c1;
  outline: none;
  box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
}

.bg-light-surface {
  background-color: #f8fafc;
}

.text-theme {
  color: #00a0c1 !important;
}

.role-header-title {
  font-size: 0.85rem;
}

.mode-toggle-group .btn {
  font-size: 0.72rem;
  padding: 0.2rem 0.55rem;
  font-weight: 600;
}

.tag-pill {
  font-size: 0.76rem;
  padding: 0.35rem 0.65rem;
  font-weight: 600;
  border-radius: 14px;
  transition: all 0.15s;
}

.pointer {
  cursor: pointer;
}

.cloud-service-card {
  border-left: 3px solid #00a0c1 !important;
}

/* Pure Toggle Switch */
.pure-toggle {
  position: relative;
  display: inline-block;
  width: 38px;
  height: 20px;
}

.pure-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.pure-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: 0.25s;
  border-radius: 20px;
}

.pure-slider:before {
  position: absolute;
  content: "";
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.25s;
  border-radius: 50%;
}

.pure-toggle input:checked + .pure-slider {
  background-color: #00a0c1;
}

.pure-toggle input:checked + .pure-slider:before {
  transform: translateX(18px);
}

/* Footer */
.modal-footer {
  padding: 0.85rem 1.5rem;
  border-top: 1px solid #f1f5f9;
  background-color: #ffffff;
}
</style>
